/**
 * 한 사람의 남은 일과 주·요일 부하.
 * 화면은 이 결과를 차트와 목록으로만 그린다.
 */

import { addDays, isISODate, toDay, weekdayOfDay, type ISODate } from "@/engine/date";

/** 차트에 그리는 과거 주(이번 주 제외). */
const PAST_WEEKS = 8;
/** 앞으로 최소한 보여줄 주(이번 주 포함). */
const MIN_FUTURE_WEEKS = 8;
/** 마지막 마감이 멀어도 이 주까지만 늘린다. */
const MAX_FUTURE_WEEKS = 16;

export type LoadKind = "execution" | "schedule";

export type LoadProject = {
  id: string;
  name: string;
  execution: { id: string; title: string; status?: string; due?: string; completedAt?: string; assignees: string[] }[];
  schedule: { id: string; title: string; resourceId?: string; assignee?: string; percent: number; plannedMM?: number; finish?: string; summary: boolean }[];
};

export type LoadMark = {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  kind: LoadKind;
  /** 끝나기로 한 날. 완료는 완료일, 일정은 종료일. */
  date?: ISODate;
  done: boolean;
  overdue: boolean;
  status?: string;
  percent?: number;
  plannedMM?: number;
};

export type LoadWeek = {
  start: ISODate;
  done: number;
  open: number;
  overdue: number;
};

export type LoadDay = {
  date: ISODate;
  /** 0=일 … 6=토 */
  weekday: number;
  count: number;
  overdue: boolean;
  titles: string[];
};

export type PersonLoad = {
  remaining: LoadMark[];
  doneCount: number;
  overdueCount: number;
  /** 끝나지 않은 일정 작업의 남은 공수. */
  remainingMM: number;
  nextDue?: ISODate;
  /** 앞으로 끝날 작업을 그 구간 주로 나눈 값. */
  pacePerWeek: number;
  /** 구간에 일이 가장 많은 요일. 없으면 null. */
  busiestWeekday: number | null;
  weeks: LoadWeek[];
  days: LoadDay[];
};

export function weekStartMonday(iso: ISODate): ISODate {
  const day = toDay(iso);
  const dow = weekdayOfDay(day);
  const delta = dow === 0 ? -6 : 1 - dow;
  return addDays(iso, delta);
}

function asDay(value?: string): ISODate | undefined {
  if (!value) return undefined;
  const day = value.slice(0, 10);
  return isISODate(day) ? day : undefined;
}

function paceOf(dates: ISODate[], today: ISODate): number {
  if (dates.length === 0) return 0;
  const start = weekStartMonday(today);
  const last = dates.reduce((max, date) => {
    const week = weekStartMonday(date);
    return week > max ? week : max;
  }, start);
  const span = Math.max(1, Math.round((toDay(last) - toDay(start)) / 7) + 1);
  return dates.length / span;
}

function windowWeeks(today: ISODate, dates: ISODate[]): ISODate[] {
  const thisWeek = weekStartMonday(today);
  let last = addDays(thisWeek, 7 * (MIN_FUTURE_WEEKS - 1));
  const cap = addDays(thisWeek, 7 * (MAX_FUTURE_WEEKS - 1));
  for (const date of dates) {
    const week = weekStartMonday(date);
    if (week > last) last = week;
  }
  if (last > cap) last = cap;
  const start = addDays(thisWeek, -7 * PAST_WEEKS);
  const weeks: ISODate[] = [];
  for (let week = start; week <= last; week = addDays(week, 7)) weeks.push(week);
  return weeks;
}

/** 배정된 실행 작업과 일정 리프. 요약 작업은 넣지 않는다. */
export function loadMarks(projects: LoadProject[], resourceId: string, name: string, today: ISODate): LoadMark[] {
  const marks: LoadMark[] = [];
  for (const project of projects) {
    for (const task of project.execution) {
      if (!task.assignees.includes(name)) continue;
      const done = task.status === "done";
      const date = done ? asDay(task.completedAt) ?? asDay(task.due) : asDay(task.due);
      marks.push({
        id: task.id,
        projectId: project.id,
        projectName: project.name,
        title: task.title,
        kind: "execution",
        date,
        done,
        overdue: !done && !!date && date < today,
        status: task.status,
        plannedMM: undefined,
      });
    }
    for (const task of project.schedule) {
      if (task.summary) continue;
      if (task.resourceId !== resourceId && task.assignee !== name) continue;
      const done = task.percent >= 100;
      const date = asDay(task.finish);
      marks.push({
        id: task.id,
        projectId: project.id,
        projectName: project.name,
        title: task.title,
        kind: "schedule",
        date,
        done,
        overdue: !done && !!date && date < today,
        percent: task.percent,
        plannedMM: task.plannedMM,
      });
    }
  }
  return marks;
}

export function personLoad(marks: LoadMark[], today: ISODate): PersonLoad {
  const remaining = marks
    .filter((mark) => !mark.done)
    .sort((a, b) => (a.date ?? "9999").localeCompare(b.date ?? "9999") || a.title.localeCompare(b.title, "ko"));
  const doneCount = marks.filter((mark) => mark.done).length;
  const overdueCount = remaining.filter((mark) => mark.overdue).length;
  const remainingMM = remaining.reduce((sum, mark) => {
    if (mark.kind !== "schedule" || mark.plannedMM === undefined) return sum;
    const left = Math.max(0, 1 - Math.min(100, mark.percent ?? 0) / 100);
    return sum + mark.plannedMM * left;
  }, 0);
  const nextDue = remaining.find((mark) => mark.date)?.date;
  const futureDates = remaining.flatMap((mark) => (mark.date && mark.date >= today ? [mark.date] : []));
  const weeks = windowWeeks(today, marks.flatMap((mark) => (mark.date ? [mark.date] : [])));
  const weekRows: LoadWeek[] = weeks.map((start) => ({ start, done: 0, open: 0, overdue: 0 }));
  const byWeek = new Map(weekRows.map((row) => [row.start, row]));
  const dayCount = new Map<string, { count: number; overdue: boolean; titles: string[] }>();
  for (const mark of marks) {
    if (!mark.date) continue;
    const week = byWeek.get(weekStartMonday(mark.date));
    if (week) {
      if (mark.done) week.done += 1;
      else if (mark.overdue) week.overdue += 1;
      else week.open += 1;
    }
    const day = dayCount.get(mark.date) ?? { count: 0, overdue: false, titles: [] };
    day.count += 1;
    day.overdue = day.overdue || mark.overdue;
    if (day.titles.length < 3) day.titles.push(mark.title || "제목 없음");
    dayCount.set(mark.date, day);
  }
  const days: LoadDay[] = [];
  const weekdayTotals = [0, 0, 0, 0, 0, 0, 0];
  for (const start of weeks) {
    for (let offset = 0; offset < 7; offset += 1) {
      const date = addDays(start, offset);
      const weekday = weekdayOfDay(toDay(date));
      const hit = dayCount.get(date);
      const count = hit?.count ?? 0;
      weekdayTotals[weekday] = (weekdayTotals[weekday] ?? 0) + count;
      days.push({ date, weekday, count, overdue: hit?.overdue ?? false, titles: hit?.titles ?? [] });
    }
  }
  const peak = Math.max(...weekdayTotals);
  const busiestWeekday = peak > 0 ? weekdayTotals.indexOf(peak) : null;
  return {
    remaining,
    doneCount,
    overdueCount,
    remainingMM: Math.round(remainingMM * 100) / 100,
    nextDue,
    pacePerWeek: Math.round(paceOf(futureDates, today) * 10) / 10,
    busiestWeekday,
    weeks: weekRows,
    days,
  };
}
